/**
 * Brief Import Mapper — Deterministic Bridge
 *
 * Pure JavaScript module that converts estate-plan-import/v1 JSON
 * into the exact payload generateDocumentsDirect() expects.
 *
 * v2: Uses the fiduciary engine (normalizeFromImport → validateCanonicalModel
 * → computeRenderVariables) as the single source of truth. Supports
 * v1.0 (flat namedRole), v1.1 (fiduciaryTier), and v1.2 (v1.1 plus optional
 * matter_facts / spouse_matter_facts / firm_settings blocks that drive the
 * post-execution To-Do List and firm-level paralegal contact info).
 *
 * No AI. No API calls. No side effects.
 */

import { normalizeFromImport } from '../config/fiduciarySchema.js';
import { validateCanonicalModel } from '../config/fiduciaryValidation.js';
import { computeRenderVariables, buildGenerationManifest } from '../config/fiduciaryRender.js';
import { isRoleAllowed, getRoleCondition, isMarriedPackage } from '../config/packageCapabilities.js';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SUPPORTED_VERSIONS = ['1.0', '1.1', '1.2'];

const SUPPORTED_PACKAGES = [
  'Disclaimer Package',
  'Probate Avoidance Package',
  'Fractional QTIP Package',
];

const ALL_KNOWN_PACKAGES = [
  'Disclaimer Package',
  'Probate Avoidance Package',
  'Fractional QTIP Package',
  'Healthcare Documents Package',
];

const TRUST_INSTRUMENT_TYPES = ['original', 'amended_and_restated'];

// Package → template path mapping (mirrors metadata.json package_document_mapping)
const PACKAGE_DOCUMENT_MAPPING = {
  'Disclaimer Package': {
    'Will': 'wills/will-pourover.docx',
    'Trust': 'trusts/trust-disclaimer.docx',
    'Financial Power of Attorney': 'powers/power-of-attorney-financial.docx',
    'Healthcare Power of Attorney': 'powers/power-of-attorney-healthcare.docx',
    'HIPAA Authorization': 'authorizations/HIPAA.docx',
    'Certificate of Trust': 'trusts/certificate-of-trust-disclaimer.docx',
  },
  'Probate Avoidance Package': {
    'Will': 'wills/will-probate-avoidance.docx',
    'Trust': 'trusts/trust-probate-avoidance.docx',
    'Financial Power of Attorney': 'powers/power-of-attorney-financial-single.docx',
    'Healthcare Power of Attorney': 'powers/power-of-attorney-healthcare.docx',
    'HIPAA Authorization': 'authorizations/HIPAA-single.docx',
    'Certificate of Trust': 'trusts/certificate-of-trust-probate-avoidance.docx',
  },
  'Fractional QTIP Package': {
    'Will': 'wills/will-pourover.docx',
    'Trust': 'trusts/trust-fractional-qtip.docx',
    'Financial Power of Attorney': 'powers/power-of-attorney-financial.docx',
    'Healthcare Power of Attorney': 'powers/power-of-attorney-healthcare.docx',
    'HIPAA Authorization': 'authorizations/HIPAA.docx',
    'Certificate of Trust': 'trusts/certificate-of-trust-fractional-qtip.docx',
  },
};

// Opt-in document: To-Do List is household-scoped and generated on demand
// (not bundled with every package). Caller sets generate_todo_list=true on
// the generation payload to trigger rendering of the package-appropriate
// variant; see DocOrchestrationGenerator for the Lambda-side handler.
export const TODO_LIST_DOCUMENT_MAPPING = {
  'Disclaimer Package': 'outlines/todo-list-disclaimer.docx',
  'Probate Avoidance Package': 'outlines/todo-list-probate-avoidance.docx',
  'Fractional QTIP Package': 'outlines/todo-list-fractional-qtip.docx',
};

export { PACKAGE_DOCUMENT_MAPPING };

// Package → engagement-letter template path. Kept separate from
// PACKAGE_DOCUMENT_MAPPING because the EL is a standalone document — not
// part of the package bundle — and that separation is the only thing
// preventing the will/trust/POA generation flow from looping in a partial
// EL render whenever an attorney generates a package before drafting facts
// are complete. Adding a key here registers a new EL variant.
export const ENGAGEMENT_LETTER_TEMPLATE_PATHS = {
  'Disclaimer Package': 'engagement_letters/engagement-letter-disclaimer.docx',
};

// Package → { documentName: templatePath } for the engagement-letter PACKAGE
// — the EL request now produces two coordinated artifacts: the legal
// Engagement Letter and the client-facing Planning Confirmation. The
// orchestrator renders every entry here and returns a packageUrls map.
// Mirrors metadata.json's engagement_letter_package_mapping.
export const ENGAGEMENT_LETTER_PACKAGE_TEMPLATES = {
  'Disclaimer Package': {
    'Engagement Letter': 'engagement_letters/engagement-letter-disclaimer.docx',
    'Planning Confirmation': 'engagement_letters/planning-confirmation.docx',
  },
};

// Planning Confirmation is package-agnostic — one template handles every
// package because the prose comes from the import JSON's
// planning_confirmation block and the fiduciary section conditionals
// drop empty rows. Standalone path lets the user regenerate just the
// PC after additional client info arrives, without re-rendering the EL.
export const PLANNING_CONFIRMATION_TEMPLATE = 'engagement_letters/planning-confirmation.docx';

// Approved relationship vocabulary (from validation_rules.json)
const RELATIONSHIP_VOCABULARY = new Set([
  'spouse', 'wife', 'husband', 'partner', 'child', 'son', 'daughter',
  'parent', 'father', 'mother', 'sibling', 'brother', 'sister',
  'grandparent', 'grandmother', 'grandfather', 'grandchild', 'grandson',
  'granddaughter', 'aunt', 'uncle', 'niece', 'nephew', 'cousin',
  'friend', 'neighbor', 'colleague', 'coworker', 'associate',
  'attorney', 'lawyer', 'accountant', 'advisor', 'agent',
  'son-in-law', 'daughter-in-law', 'brother-in-law', 'sister-in-law',
  'father-in-law', 'mother-in-law', 'stepson', 'stepdaughter',
  'stepfather', 'stepmother', 'financial advisor',
]);

// ---------------------------------------------------------------------------
// 1. Schema Validation
// ---------------------------------------------------------------------------

/**
 * Validate the import JSON conforms to estate-plan-import schema.
 * @param {object} json - Parsed JSON object
 * @returns {{ valid: boolean, errors: string[] }}
 */
export function validateImportSchema(json) {
  const errors = [];

  if (!json || typeof json !== 'object') {
    return { valid: false, errors: ['Input is not a valid JSON object.'] };
  }

  if (json.schema_name !== 'estate-plan-import') {
    errors.push(`Invalid schema_name: expected "estate-plan-import", got "${json.schema_name}".`);
  }

  if (!SUPPORTED_VERSIONS.includes(json.schema_version)) {
    errors.push(
      `Unsupported schema_version "${json.schema_version}". Supported: ${SUPPORTED_VERSIONS.join(', ')}.`
    );
  }

  if (!json.client || typeof json.client !== 'object') {
    errors.push('Missing required field: client.');
  } else if (!json.client.full_name || typeof json.client.full_name !== 'string' || !json.client.full_name.trim()) {
    errors.push('Missing required field: client.full_name.');
  }

  if (!json.fiduciaries || typeof json.fiduciaries !== 'object') {
    errors.push('Missing required field: fiduciaries.');
  }

  // package_candidate can be null (attorney selects manually) but key must exist
  if (!('package_candidate' in json)) {
    errors.push('Missing required field: package_candidate (can be null if attorney will select).');
  }

  // v1.2 additive blocks — if present, must be plain objects. Individual
  // keys are boolean-coerced downstream in the fiduciary engine, so we
  // only enforce structural shape here; unknown keys are allowed.
  if ('matter_facts' in json && json.matter_facts !== null
      && (typeof json.matter_facts !== 'object' || Array.isArray(json.matter_facts))) {
    errors.push('matter_facts, if present, must be an object keyed by has_* booleans.');
  }
  if ('spouse_matter_facts' in json && json.spouse_matter_facts !== null
      && (typeof json.spouse_matter_facts !== 'object' || Array.isArray(json.spouse_matter_facts))) {
    errors.push('spouse_matter_facts, if present, must be an object keyed by has_* booleans.');
  }
  if ('firm_settings' in json && json.firm_settings !== null
      && (typeof json.firm_settings !== 'object' || Array.isArray(json.firm_settings))) {
    errors.push('firm_settings, if present, must be an object.');
  }
  if ('trust_instrument_type' in json && json.trust_instrument_type !== null
      && !TRUST_INSTRUMENT_TYPES.includes(json.trust_instrument_type)) {
    errors.push('trust_instrument_type, if present, must be "original" or "amended_and_restated".');
  }

  return { valid: errors.length === 0, errors };
}

// ---------------------------------------------------------------------------
// 2. Package Resolution
// ---------------------------------------------------------------------------

/**
 * Validate and resolve the proposed package type.
 * @param {object} importData - Validated import JSON
 * @returns {{ package: string|null, errors: string[], warnings: string[], confirmations: string[] }}
 */
export function resolvePackage(importData) {
  const errors = [];
  const warnings = [];
  const confirmations = [];
  const candidate = importData.package_candidate;
  const maritalStatus = importData.client?.marital_status;

  if (candidate === null || candidate === undefined) {
    errors.push('Package type required. Select Disclaimer Package, Fractional QTIP Package, or Probate Avoidance Package.');
    return { package: null, errors, warnings, confirmations };
  }

  if (!ALL_KNOWN_PACKAGES.includes(candidate)) {
    errors.push(`Unknown package type: "${candidate}".`);
    return { package: null, errors, warnings, confirmations };
  }

  if (!SUPPORTED_PACKAGES.includes(candidate)) {
    errors.push(
      `"${candidate}" is not yet supported for import. Use Disclaimer Package, Fractional QTIP Package, Probate Avoidance Package, or manual entry.`
    );
    return { package: null, errors, warnings, confirmations };
  }

  // Cross-check package against marital status
  if (candidate === 'Disclaimer Package' && maritalStatus && maritalStatus !== 'married') {
    warnings.push(
      `Disclaimer Package is typically for married couples. Client marital status is "${maritalStatus}". Please confirm this is correct.`
    );
  }

  if (candidate === 'Probate Avoidance Package' && maritalStatus === 'married') {
    warnings.push(
      'Probate Avoidance Package is typically for single individuals. Client is married. Please confirm this is correct.'
    );
  }

  if (candidate === 'Fractional QTIP Package' && maritalStatus && maritalStatus !== 'married') {
    warnings.push(
      `Fractional QTIP Package is for married couples. Client marital status is "${maritalStatus}". Please confirm this is correct.`
    );
  }

  // Package selection always requires confirmation
  confirmations.push(`Confirm "${candidate}" is the correct package for this client.`);

  return { package: candidate, errors, warnings, confirmations };
}

// ---------------------------------------------------------------------------
// 3. Gateway Answer Derivation (via Fiduciary Engine)
// ---------------------------------------------------------------------------

/**
 * Derive gateway question answers from the import data.
 * Uses the fiduciary engine's canonical model for per-role successor counts.
 *
 * @param {object} importData - Validated import JSON
 * @returns {object} Gateway answers including per-role counts, ages, survivorship
 */
export function deriveGatewayAnswers(importData) {
  const confirmations = [];
  const model = normalizeFromImport(importData);

  // has_minor_children
  let has_minor_children = model.packageConfig.has_minor_children;
  if (has_minor_children === null) {
    confirmations.push(
      'Could not determine if any children are minors. Please confirm whether minor children exist.'
    );
  }

  // Per-role successor counts (from canonical model)
  const trustee_successor_count = model.roleAssignments.trustee?.tiers?.length || 0;
  const executor_successor_count = Math.max(0, (model.roleAssignments.executor?.tiers?.length || 0) - 1);
  const trust_protector_successor_count = model.roleAssignments.trust_protector?.tiers?.length || 0;
  const estate_protector_successor_count = model.roleAssignments.estate_protector?.tiers?.length || 0;

  // Backward compat: single successor_levels = max across roles, capped at 3
  const successor_levels = Math.min(
    3,
    Math.max(1, trustee_successor_count, executor_successor_count,
      trust_protector_successor_count)
  );

  return {
    has_minor_children,
    successor_levels,
    trustee_successor_count,
    executor_successor_count,
    trust_protector_successor_count,
    estate_protector_successor_count,
    survivorship_direction: model.packageConfig.survivorship_direction,
    trust_instrument_type: model.packageConfig.trust_instrument_type,
    ...model.trustConfig,
    confirmations,
  };
}

// ---------------------------------------------------------------------------
// 4. Placeholder Flattening (via Fiduciary Engine)
// ---------------------------------------------------------------------------

/**
 * Convert import JSON into the flat SED placeholder variables dict.
 * Uses the fiduciary engine as the single source of truth.
 *
 * @param {object} importData - Validated import JSON
 * @param {string} packageType - Resolved package type
 * @returns {object} Flat key-value placeholder dict for generateDocumentsDirect()
 */
export function flattenToPlaceholders(importData, packageType) {
  // Build canonical model
  const model = normalizeFromImport(importData);

  // Compute render variables (single source of truth)
  const vars = computeRenderVariables(model);

  // Estate protector name (render engine uses people registry, but template
  // expects just the name without relationship for estate protector)
  if (model.roleAssignments.estate_protector?.tiers?.[0]?.people?.[0]) {
    const personId = model.roleAssignments.estate_protector.tiers[0].people[0];
    const person = model.people[personId];
    if (person) {
      vars.estate_protector = person.name;
    }
  }

  return vars;
}

// ---------------------------------------------------------------------------
// 5. Validation for Generation (via Fiduciary Engine)
// ---------------------------------------------------------------------------

/**
 * Validate the flattened variables for generation readiness.
 * Delegates structural validation to the fiduciary engine's validateCanonicalModel(),
 * then checks for empty required template fields.
 *
 * @param {object} variables - Flattened placeholder dict from flattenToPlaceholders()
 * @param {string} packageType - Resolved package type
 * @param {object} importData - Original import JSON
 * @returns {{ missingFields: object[], warnings: object[], correctionPrompt: string|null, canGenerate: boolean }}
 */
export function validateForGeneration(variables, packageType, importData) {
  const missingFields = [];
  const warnings = [];

  // Run canonical model validation
  const model = normalizeFromImport(importData);
  const modelValidation = validateCanonicalModel(model);

  // Convert model errors to missingFields format
  for (const error of modelValidation.errors) {
    missingFields.push({ field: error.field, label: error.message });
  }

  // Convert model warnings
  for (const warning of modelValidation.warnings) {
    warnings.push({ field: warning.field, message: warning.message });
  }

  // Check critical template fields that must be non-empty
  const criticalFields = [
    ['client', 'Client full name'],
    ['client_residence_county', 'County of residence'],
    ['signing_date', 'Signing date'],
    ['rlt_date', 'Revocable living trust date'],
    ['client_date_of_birth', 'Client date of birth (for HIPAA)'],
  ];

  // Spouse required for married packages
  if (isMarriedPackage(packageType)) {
    criticalFields.push(['spouse', 'Spouse full name']);
  }

  for (const [field, label] of criticalFields) {
    const val = variables[field];
    if (val === undefined || val === null || (typeof val === 'string' && !val.trim())) {
      // Avoid duplicates from model validation
      if (!missingFields.some(mf => mf.field === field)) {
        missingFields.push({ field, label });
      }
    }
  }

  // Surface ambiguities from import JSON as warnings
  for (const amb of (importData.ambiguities || [])) {
    warnings.push({ field: amb.field, message: amb.detail });
  }

  // Generate correction prompt
  let correctionPrompt = null;
  if (missingFields.length > 0) {
    const lines = missingFields.map(f => `- ${f.label} (field: ${f.field})`);
    correctionPrompt =
      `The following fields are required for the ${packageType} but are missing or empty in the JSON.\n` +
      `Please update the estate-plan-import JSON to include these fields:\n\n` +
      lines.join('\n') +
      `\n\nThe JSON must conform to schema estate-plan-import v1.0, v1.1, or v1.2. ` +
      `All fields listed above must have non-empty values. ` +
      `If you only need to render the Engagement Letter (which tolerates missing drafting facts), ` +
      `select "Engagement Letter only" on the import page instead of the full package.`;
  }

  return {
    missingFields,
    warnings,
    correctionPrompt,
    canGenerate: missingFields.length === 0,
  };
}

// ---------------------------------------------------------------------------
// 5b. Engagement Letter — narrowly-scoped validation
// ---------------------------------------------------------------------------

/**
 * Validate the JSON for engagement-letter generation only.
 *
 * Deliberately narrower than validateForGeneration(): the EL is meant to
 * render before the drafting questionnaire is complete, so drafting-specific
 * fields (signing_date, rlt_date, HIPAA DOB, full fiduciary tree) are NOT
 * required here. The only blockers are the fields that materially identify
 * the engagement: client name, package, and spouse name (for married packages).
 *
 * This is a SEPARATE function from validateForGeneration so the will/trust/
 * POA validation cannot be inadvertently weakened by passing an EL flag.
 *
 * @param {object} importData - Parsed import JSON
 * @returns {{ missingFields: object[], warnings: object[], correctionPrompt: string|null, canGenerate: boolean }}
 */
export function validateForEngagementLetter(importData) {
  const missingFields = [];
  const warnings = [];

  const clientName = importData?.client?.full_name;
  if (typeof clientName !== 'string' || !clientName.trim()) {
    missingFields.push({ field: 'client.full_name', label: 'Client full name' });
  }

  const pkg = importData?.package_candidate;
  if (!pkg) {
    missingFields.push({ field: 'package_candidate', label: 'Package selection' });
  } else if (!ENGAGEMENT_LETTER_TEMPLATE_PATHS[pkg]) {
    missingFields.push({
      field: 'package_candidate',
      label: `No engagement letter variant for package: ${pkg}`,
    });
  }

  if (pkg && isMarriedPackage(pkg)) {
    const spouseName = importData?.spouse?.full_name;
    if (typeof spouseName !== 'string' || !spouseName.trim()) {
      missingFields.push({ field: 'spouse.full_name', label: 'Spouse full name (married package)' });
    }
  }

  // Surface ambiguities from import JSON as warnings (informational only).
  for (const amb of (importData?.ambiguities || [])) {
    warnings.push({ field: amb.field, message: amb.detail });
  }

  return {
    missingFields,
    warnings,
    correctionPrompt: null,
    canGenerate: missingFields.length === 0,
  };
}

// ---------------------------------------------------------------------------
// 5c. Planning Confirmation — narrowly-scoped, lenient validation
// ---------------------------------------------------------------------------

/**
 * Validate JSON for standalone Planning Confirmation rendering.
 *
 * The Planning Confirmation can stand alone for a few reasons:
 *   - The Microsoft Automation helper now ships a separate PC sub-brief
 *     alongside the EL sub-brief, and that sub-brief may carry only the
 *     planning_confirmation prose + a subset of the fiduciary tree.
 *   - The attorney may want to regenerate just the PC after additional
 *     client info arrives, without re-issuing the EL.
 *
 * Hence: lenient validation. Only client.full_name is required (used in
 * the greeting line). package_candidate, fiduciaries, dates, and the
 * planning_confirmation block itself are all OPTIONAL. When the
 * planning_confirmation block is absent, the SED defaults in the
 * fiduciary render module produce sensible fallback text. When
 * fiduciaries are absent, the role rows drop silently via the
 * paragraph-level conditionals in the template.
 *
 * @param {object} importData - Parsed import JSON
 * @returns {{ missingFields: object[], warnings: object[], correctionPrompt: string|null, canGenerate: boolean }}
 */
export function validateForPlanningConfirmation(importData) {
  const missingFields = [];
  const warnings = [];

  const clientName = importData?.client?.full_name;
  if (typeof clientName !== 'string' || !clientName.trim()) {
    missingFields.push({ field: 'client.full_name', label: 'Client full name' });
  }

  // Spouse / package / fiduciaries are NOT required — see docstring.

  if (!importData?.planning_confirmation) {
    warnings.push({
      field: 'planning_confirmation',
      message: 'No planning_confirmation block in the brief. The document will render with default placeholder copy in the Plan Assumptions and Engagement Timeline sections.',
    });
  }

  // Surface ambiguities as informational warnings.
  for (const amb of (importData?.ambiguities || [])) {
    warnings.push({ field: amb.field, message: amb.detail });
  }

  return {
    missingFields,
    warnings,
    correctionPrompt: null,
    canGenerate: missingFields.length === 0,
  };
}

// ---------------------------------------------------------------------------
// 6. Build Generation Payload
// ---------------------------------------------------------------------------

/**
 * Assemble the exact payload that generateDocumentsDirect() expects.
 * @param {object} importData - Validated import JSON
 * @param {string} packageType - Resolved and confirmed package type
 * @param {string} sessionId - Active session ID
 * @param {string} userSub - Cognito user sub
 * @param {string} firmId - Firm identifier
 * @param {object} [options]
 * @param {boolean} [options.generateTodoList=false] - Ask the orchestrator
 *   to render the package's post-execution To-Do List in addition to the
 *   core package documents.
 * @param {object} [options.matterFacts] - Boolean has_* drivers for the
 *   current client (has_life_insurance, has_retirement_accounts, etc.).
 *   Merged into importData before normalization so they flow through to
 *   render variables.
 * @param {object} [options.spouseMatterFacts] - Same shape as matterFacts
 *   but for the spouse (only meaningful for married packages).
 * @param {object} [options.spousePlanDetails] - Optional spouse plan info
 *   used by the To-Do List: { spouse_package_type, spouse_rlt_date }.
 *   Appended to the variables dict for template access.
 * @returns {object} Payload for generateDocumentsDirect()
 */
export function buildGenerationPayload(importData, packageType, sessionId, userSub, firmId, options = {}) {
  const {
    generateTodoList = false,
    matterFacts = null,
    spouseMatterFacts = null,
    spousePlanDetails = null,
  } = options;

  // Matter-facts get merged into importData so normalizeFromImport picks
  // them up and the canonical-model → render-vars pipeline emits the has_*
  // flags the templates key on. Keeping this as a non-destructive overlay
  // preserves the caller's importData reference.
  const augmentedImport = (matterFacts || spouseMatterFacts)
    ? {
        ...importData,
        ...(matterFacts ? { matter_facts: matterFacts } : {}),
        ...(spouseMatterFacts ? { spouse_matter_facts: spouseMatterFacts } : {}),
      }
    : importData;

  const variables = flattenToPlaceholders(augmentedImport, packageType);

  // Spouse plan details (spouse_package_type, spouse_rlt_date) are strictly
  // template-level variables, not canonical-model fields. Drop them straight
  // onto the flat variables dict so the To-Do List renders correctly when
  // the spouses have different plan types or trust dates.
  if (spousePlanDetails) {
    if (spousePlanDetails.spouse_package_type) {
      variables.spouse_package_type = spousePlanDetails.spouse_package_type;
    }
    if (spousePlanDetails.spouse_rlt_date) {
      variables.spouse_rlt_date = spousePlanDetails.spouse_rlt_date;
    }
  }

  const templateMapping = PACKAGE_DOCUMENT_MAPPING[packageType];
  if (!templateMapping) {
    throw new Error(`No template mapping found for package: ${packageType}`);
  }

  const packageDocs = Object.keys(templateMapping);
  const placeholders = Object.keys(variables).filter(k => variables[k] !== '');

  return {
    documentType: packageType,
    variables,
    userMessage: '',
    isPackage: true,
    packageDocs,
    packageTemplatePaths: { ...templateMapping },
    templatePath: '',
    placeholders,
    docMetadata: {},
    sessionId,
    userSub,
    firmId,
    generateTodoList,
  };
}

// ---------------------------------------------------------------------------
// 6b. Build Engagement Letter Payload (standalone single-document path)
// ---------------------------------------------------------------------------

/**
 * Assemble the generateDocumentsDirect() payload for a standalone engagement
 * letter render.
 *
 * This payload is intentionally NOT a package bundle: isPackage=false and
 * the EL is requested by its own documentType. That separation is what
 * prevents the EL's lenient validation from leaking into the will/trust
 * generation flow.
 *
 * @param {object} importData - Validated import JSON
 * @param {string} packageType - Package type (controls EL variant selection)
 * @param {string} sessionId - Active session ID
 * @param {string} userSub - Cognito user sub
 * @param {string} firmId - Firm identifier
 * @returns {object} Payload for generateDocumentsDirect()
 */
export function buildEngagementLetterPayload(importData, packageType, sessionId, userSub, firmId) {
  const templatePath = ENGAGEMENT_LETTER_TEMPLATE_PATHS[packageType];
  if (!templatePath) {
    throw new Error(`No engagement letter template registered for package: ${packageType}`);
  }

  const variables = flattenToPlaceholders(importData, packageType);
  const placeholders = Object.keys(variables).filter(k => variables[k] !== '');

  // packageTemplates: when present, the orchestrator renders every
  // listed template with the SAME variables dict and returns one URL
  // per document in packageUrls. When absent, falls back to the
  // single-document templatePath path (legacy).
  const packageTemplates = ENGAGEMENT_LETTER_PACKAGE_TEMPLATES[packageType] || null;

  return {
    documentType: 'Engagement Letter',
    variables,
    userMessage: '',
    isPackage: false,
    packageContext: packageType,
    templatePath,
    packageTemplates,
    placeholders,
    docMetadata: {},
    sessionId,
    userSub,
    firmId,
  };
}

// Forward-looking alias. The EL request now produces a two-document
// package (Engagement Letter + Planning Confirmation). New call sites
// should prefer this name; buildEngagementLetterPayload remains as the
// legacy export and is removed once all callers migrate.
export const buildEngagementLetterPackagePayload = buildEngagementLetterPayload;

/**
 * Assemble the generateDocumentsDirect() payload for a STANDALONE
 * Planning Confirmation render.
 *
 * Used when the attorney chooses "Planning Confirmation Only" on the
 * import page — typically because Microsoft Automation produced a PC
 * sub-brief separately from the EL sub-brief, or because additional
 * client info arrived and the PC needs to be regenerated.
 *
 * Lenient: works whether or not the brief carries package_candidate,
 * fiduciaries, or a planning_confirmation block. When package_candidate
 * is absent, we fall back to 'Disclaimer Package' purely for variable
 * computation — the PC template itself is package-agnostic, so this
 * default does NOT affect the rendered output beyond what's already
 * displayed in the document.
 *
 * @param {object} importData - Validated import JSON
 * @param {string} sessionId - Active session ID
 * @param {string} userSub - Cognito user sub
 * @param {string} firmId - Firm identifier
 * @returns {object} Payload for generateDocumentsDirect()
 */
export function buildPlanningConfirmationPayload(importData, sessionId, userSub, firmId) {
  // Use the brief's package_candidate when present so role-tier rendering
  // (e.g., Co-Trustees title clause) matches the same package context the
  // EL would use. Default ensures variable computation completes when the
  // PC sub-brief intentionally omits it.
  const packageForVars = importData?.package_candidate || 'Disclaimer Package';

  const variables = flattenToPlaceholders(importData, packageForVars);
  const placeholders = Object.keys(variables).filter(k => variables[k] !== '');

  return {
    documentType: 'Planning Confirmation',
    variables,
    userMessage: '',
    isPackage: false,
    packageContext: packageForVars,
    templatePath: PLANNING_CONFIRMATION_TEMPLATE,
    placeholders,
    docMetadata: {},
    sessionId,
    userSub,
    firmId,
  };
}

// ---------------------------------------------------------------------------
// 7. Build Generation Manifest
// ---------------------------------------------------------------------------

/**
 * Build the deterministic generation manifest for audit/QC.
 * @param {object} importData - Validated import JSON
 * @returns {object} Generation manifest
 */
export function buildManifest(importData) {
  const model = normalizeFromImport(importData);
  const renderVars = computeRenderVariables(model);
  return buildGenerationManifest(model, renderVars);
}

// ---------------------------------------------------------------------------
// 8. Full Import Pipeline (convenience)
// ---------------------------------------------------------------------------

/**
 * Run the full deterministic import pipeline.
 * Does NOT call generateDocumentsDirect() — returns the payload and validation results
 * for the review UI to present to the attorney.
 *
 * @param {object} json - Raw parsed JSON from import
 * @returns {object} { schemaValid, schemaErrors, packageResult, gatewayAnswers, variables, validation, manifest, payload }
 */
export function processImport(json) {
  // Step 1: Schema validation
  const schema = validateImportSchema(json);
  if (!schema.valid) {
    return {
      schemaValid: false,
      schemaErrors: schema.errors,
      packageResult: null,
      gatewayAnswers: null,
      variables: null,
      validation: null,
      manifest: null,
      payload: null,
    };
  }

  // Step 2: Package resolution
  const packageResult = resolvePackage(json);

  // If package has errors, we can still show data but can't build payload
  if (packageResult.errors.length > 0) {
    const variables = flattenToPlaceholders(json, packageResult.package || 'Disclaimer Package');
    return {
      schemaValid: true,
      schemaErrors: [],
      packageResult,
      gatewayAnswers: deriveGatewayAnswers(json),
      variables,
      validation: null,
      manifest: null,
      payload: null,
    };
  }

  const packageType = packageResult.package;

  // Step 3: Gateway answers (per-role counts, ages, survivorship)
  const gatewayAnswers = deriveGatewayAnswers(json);

  // Step 4: Flatten to placeholders via fiduciary engine
  const variables = flattenToPlaceholders(json, packageType);

  // Step 5: Validate for generation
  const validation = validateForGeneration(variables, packageType, json);

  // Merge package-level warnings
  validation.warnings.push(...packageResult.warnings.map(w => ({ field: 'package_candidate', message: w })));

  // Step 6: Build generation manifest
  const manifest = buildManifest(json);

  return {
    schemaValid: true,
    schemaErrors: [],
    packageResult,
    gatewayAnswers,
    variables,
    validation,
    manifest,
    // payload is null until attorney confirms — call buildGenerationPayload() after review
    payload: null,
  };
}
